"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { sectionBackgroundColors } from "@/lib/utils";
import Link from "next/link";
import parse, { domToReact, HTMLReactParserOptions, Element } from "html-react-parser";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionWrapper } from "../components/SectionWraper";

interface Feature {
  icon?: string;
  title?: string;
  content?: string;
  link?: string;
}

interface Feature8Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  image?: string;
  imageAltText?: string;
  stickyTitlePanel?: boolean;
  featureItems?: Feature[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature8 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 8",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  stickyTitlePanel,
  featureItems,
  attributes,
}: Feature8Props) => {
  const sectionBackgroundColor = sectionBackgroundColors(darkMode, variant);
  const itemBackgroundColor = variantBackgroundColors(darkMode, variant);
  
  const titlePanelClass = cn(
    "mx-auto flex h-fit max-w-screen-md flex-col gap-6 text-center items-center md:text-left md:items-start ",
    stickyTitlePanel ? "md:sticky top-30" : ""
  )
  
  // Parser options to strip <a> tags when content is already inside a Link
  const parseOptionsStripLinks: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.name === 'a') {
        // Replace <a> tags with <span> to prevent nested links
        return <span className={domNode.attribs?.class}>{domToReact(domNode.children as Element[], parseOptionsStripLinks)}</span>;
      }
    }
  };
        
  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature8"
      attributes={attributes}
    >
      <div className="container">
        <div className="relative grid gap-10 md:grid-cols-2">
          <div className={titlePanelClass}>  
            {badge && (
              <Badge variant="outline" bold>
                {badge}
              </Badge>
            )}
            {title && (
              <h2>
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="paragraph-lg">
                {parse(content)}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            {featureItems && featureItems.map((feature, index) => (
              <div key={`feature-${index}`}>
                {feature.link ? (
                  <Link href={feature.link} tabIndex={-1}>
                    <div
                      className={cn(
                        "gap flex flex-col rounded-lg p-7 text-center md:text-left",
                        itemBackgroundColor
                      )}
                      data-aos="fade-up"
                      data-aos-delay={`${(index + 1) * 50}`}
                    >
                      <div className="flex flex-col lg:flex-row gap-9 md:gap-6 items-center">
                        <div className="flex flex-col gap-4">
                          {(feature.icon || feature.content) && (
                            <div className="flex flex-col gap-4 items-center md:items-start">
                              {feature.icon && (
                                <span className={cn(
                                  "flex size-12 items-center justify-center rounded-full text-brand",
                                  sectionBackgroundColor
                                )}>
                                  <span className="material-symbols-outlined">{feature.icon}</span>
                                </span>
                              )}
                              <h6>{feature.title}</h6>
                            </div>
                          )}
                          {feature.content && (
                            <div className="text-muted-foreground paragraph-base">
                              {parse(feature.content, parseOptionsStripLinks)}
                            </div>
                          )}
                        </div>
                        <div>
                          <Button variant="outline" grayBorder size="iconLg" lifted>
                            <span className="material-symbols-outlined">arrow_forward</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div
                    className={cn(
                      "gap flex flex-col rounded-lg p-7 text-center md:text-left",
                      itemBackgroundColor
                    )}
                    data-aos="fade-up"
                    data-aos-delay={`${(index + 1) * 50}`}
                  >
                    <div className="flex flex-col lg:flex-row gap-9 md:gap-6 items-center">
                      <div className="flex flex-col gap-4">
                        {(feature.icon || feature.content) && (
                          <div className="flex flex-col gap-4 items-center md:items-start">
                            {feature.icon && (
                              <span className={cn(
                                "flex size-12 items-center justify-center rounded-full text-brand",
                                sectionBackgroundColor
                              )}>
                                <span className="material-symbols-outlined">{feature.icon}</span>
                              </span>
                            )}
                            <h6>{feature.title}</h6>
                          </div>
                        )}
                        {feature.content && (
                          <div className="text-muted-foreground paragraph-base">
                            {parse(feature.content)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionWrapper>
  )
};

export { Feature8 };